import { LockKey, Warning, ArrowClockwise, ShoppingCart } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { CashierLabels } from "./types";

export function CashierSkeleton() {
  return (
    <div className="h-full flex flex-col bg-biz-bg">
      <div className="h-[68px] border-b border-biz-border bg-biz-surface-elevated px-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-biz-btn bg-biz-muted animate-pulse" />
        <div className="h-4 w-40 bg-biz-muted rounded animate-pulse" />
        <div className="ml-auto h-10 w-32 bg-biz-muted rounded-biz-btn animate-pulse" />
      </div>
      <div className="flex-1 grid grid-cols-1 md:grid-cols-[1fr_400px] min-h-0">
        <div className="p-4">
          <div className="h-11 bg-biz-muted rounded-biz-btn animate-pulse mb-3" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="h-[120px] rounded-biz-card bg-biz-muted animate-pulse" />
            ))}
          </div>
        </div>
        <div className="border-l border-biz-border bg-biz-surface p-4 space-y-3">
          <div className="h-12 bg-biz-muted rounded-biz-btn animate-pulse" />
          <div className="h-20 bg-biz-muted rounded-biz-card animate-pulse" />
          <div className="h-20 bg-biz-muted rounded-biz-card animate-pulse" />
          <div className="h-14 bg-biz-muted rounded-biz-btn animate-pulse mt-auto" />
        </div>
      </div>
    </div>
  );
}

export function CashierErrorState({
  labels, onRetry, message,
}: { labels: CashierLabels; onRetry?: () => void; message?: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12 bg-biz-bg">
      <div className="h-16 w-16 rounded-full bg-biz-danger-subtle text-biz-danger flex items-center justify-center mb-4">
        <Warning weight="fill" className="h-8 w-8" />
      </div>
      <h2 className="text-lg font-bold text-biz-foreground">{labels.states.errorTitle}</h2>
      <p className="text-sm text-biz-foreground-muted mt-1 max-w-sm">{message ?? labels.states.errorDescription}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            "mt-5 h-11 px-4 rounded-biz-btn bg-biz-primary text-biz-primary-foreground font-semibold inline-flex items-center gap-2",
            "biz-focus-ring hover:bg-biz-primary-hover transition-colors",
          )}
        >
          <ArrowClockwise className="h-4 w-4" />
          {labels.states.errorRetry}
        </button>
      )}
    </div>
  );
}

export function CashierSessionClosedState({ labels }: { labels: CashierLabels }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12 bg-biz-bg">
      <div className="h-16 w-16 rounded-full bg-biz-muted text-biz-foreground-muted flex items-center justify-center mb-4">
        <LockKey weight="fill" className="h-8 w-8" />
      </div>
      <h2 className="text-lg font-bold text-biz-foreground">{labels.states.sessionClosedTitle}</h2>
      <p className="text-sm text-biz-foreground-muted mt-1 max-w-sm">{labels.states.sessionClosedDescription}</p>
    </div>
  );
}

export function CashierEmptyCart({ labels }: { labels: CashierLabels }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-10 text-biz-foreground-muted">
      <div className="h-14 w-14 rounded-full bg-biz-muted flex items-center justify-center mb-3">
        <ShoppingCart className="h-7 w-7 text-biz-foreground-subtle" />
      </div>
      <p className="text-sm font-semibold text-biz-foreground">{labels.cart.emptyTitle}</p>
      <p className="text-xs mt-1 max-w-[220px]">{labels.cart.emptyDescription}</p>
    </div>
  );
}
